import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { format } from 'date-fns';
import { Container, MessageContainer, SendContainer, ContainerCommentScroll } from './styles';
import { isImage } from 'utils/files';
import { Icon, Loading } from 'components';
import { openNotification } from 'utils/notification';

import {
  createComment,
  listComments,
  downloadFile,
  getAnexoComment,
} from 'services/commentsClientDemand';
import { IContent, IComment, IClientDemand } from 'types/demands';
import { extname } from 'path';
import { getLoggedUser } from 'utils/storage';
import { Flex } from '@chakra-ui/react';

interface IProps {
  updateFiles?: React.Dispatch<React.SetStateAction<IContent[] | undefined>>;
  files?: IClientDemand['solicitacaoDocumentos'];
}

const CommentsTab: React.FC<IProps> = ({ updateFiles, files: docs }) => {
  const { id } = useParams<{ id: string }>();
  const inputRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [text, setText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [comments, setComments] = useState<IComment[]>([]);
  const commentsCRef = useRef<HTMLDivElement>(null);

  const handleDownload = useCallback(
    async (id: number, noCatch?: boolean) => {
      try {
        const { data } = await downloadFile(id);
        return data;
      } catch (err) {
        if (noCatch) return '';
        throw err;
      }
    },
    [id],
  );

  const handleGetFile = useCallback(
    (isImage?: boolean) => {
      if (!isImage && files.findIndex(i => !i.type.includes('image')) > -1) {
        return openNotification('Apenas 1 documento por comentário', 'warn');
      } else if (
        isImage &&
        files.reduce((acc, i) => {
          if (i.type.includes('image')) acc++;
          return acc;
        }, 0) > 3
      ) {
        return openNotification('Máximo de 4 imagens por comentário', 'warn');
      }
      inputRef.current?.setAttribute('accept', '');
      if (isImage) {
        inputRef.current?.setAttribute('accept', 'image/*');
      }
      inputRef.current?.focus();
      inputRef.current?.click();
    },
    [files],
  );

  const handleFile = useCallback(() => {
    let files = [] as File[];
    if (inputRef.current?.accept.includes('image')) {
      files = Array.from(inputRef.current?.files || []).slice(0, 4);
    } else {
      files = Array.from(inputRef.current?.files || []).slice(0, 1);
      if (files[0]?.type.includes('image')) {
        openNotification('Não subir imagem como documento', 'warn');
        inputRef.current?.setAttribute('value', '');
        inputRef.current?.setAttribute('files', '');
        return;
      }
    }
    setFiles(arr => [
      ...arr,
      ...files.filter(i => {
        if (i.size > 0xa00000) {
          openNotification('Tamanho máximo de 10mb por arquivo', 'warn');
          return false;
        } else {
          return true;
        }
      }),
    ]);
    inputRef.current?.setAttribute('value', '');
    inputRef.current?.setAttribute('files', '');
  }, []);

  const getComments = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data } = await listComments(id);
      const comments: IComment[] = [];
      for (const i of data) {
        const comment = {
          ...i,
          anexosComentario: [],
          hasDocument: i.anexosComentario.findIndex(i => !isImage(extname(i.filename))) > -1,
          hasImage: i.anexosComentario.findIndex(i => isImage(extname(i.filename))) > -1,
        } as IComment;
        for (const a of i.anexosComentario) {
          const isImg = isImage(extname(a.filename));
          let url;
          const download: any = await handleDownload(a.id, true);
          if (isImg) {
            url = URL.createObjectURL(download);
          }
          comment.anexosComentario.push({
            ...a,
            isImage: isImg,
            url,
          });
        }
        comments.push(comment);
      }
      setComments(arr => {
        const urls = arr
          .map(i => i.anexosComentario.filter(i => !!i.url).map(i => i.url as string))
          .flat();
        for (const url of urls) {
          if (url) URL.revokeObjectURL(url);
        }
        return comments;
      });
    } catch (err) {}
    setIsLoading(false);
  }, [id]);

  useEffect(() => {
    getComments();
  }, [id]);

  const handleSendComment = useCallback(async () => {
    if (!text) {
      return openNotification('Digitar comentário antes de enviar', 'warn');
    }
    setIsLoading(true);
    try {
      const formData = new FormData();

      formData.append(
        'solicitacaoComentarioDto',
        new Blob([JSON.stringify({ description: text })], { type: 'application/json' }),
      );

      files.forEach(file => formData.append('files', file));

      await createComment(id, formData);
      await getComments();
      const {
        data: { content },
      } = await getAnexoComment(id);
      if (updateFiles && docs) updateFiles([...content, ...docs]);
      setFiles([]);
      setText('');
    } catch (err) {}
    setIsLoading(false);
  }, [text, files, id, getComments]);

  const handleDonwloadFile = useCallback(
    async (id: number, filename: string, url?: string) => {
      if (isLoading) return;
      try {
        setIsLoading(true);
        if (!url) {
          const blob: any = await handleDownload(id, false);
          url = URL.createObjectURL(blob);
        }
        const a = document.createElement('a');
        a.onclick = () => {
          document.body.removeChild(a);
        };

        a.href = url;
        a.download = filename;
        a.rel = 'noopener noreferrer';
        a.target = '_blank';

        document.body.appendChild(a);

        a.click();
      } catch (err) {
        console.error(err);
        openNotification('Ocorreu um erro ao baixar o arquivo', 'error');
      } finally {
        setIsLoading(false);
      }
    },
    [handleDownload, isLoading],
  );

  return (
    <Container>
      <SendContainer style={{ marginBottom: '10px' }}>
        <div className="col">
          <textarea
            disabled={getLoggedUser().isParticipant}
            onChange={t => setText(t.target.value || '')}
            value={text}
            maxLength={140}
            placeholder="Escreva um comentário"
          />
        </div>

        <div className="col uploadBtns">
          <Flex>
            <button
              disabled={isLoading || getLoggedUser().isParticipant}
              onClick={handleGetFile.bind(null, true)}
              className="up"
            >
              <Icon $iconColor={700} size={1} name="image" marginBottom={1} color="text" />
            </button>
            <button
              disabled={isLoading || getLoggedUser().isParticipant}
              onClick={handleGetFile.bind(null, false)}
              className="up"
            >
              <Icon $iconColor={700} size={1} name="paperClip" marginBottom={1} color="text" />
            </button>
            {files.length > 0 && (
              <span title={files.map(i => i.name).join('\r\n')}>
                {files.length}
                <button disabled={getLoggedUser().isParticipant} onClick={setFiles.bind(null, [])}>
                  <Icon $iconColor={900} size={0.7} name="close" />
                </button>
              </span>
            )}
          </Flex>

          <button
            onClick={
              getLoggedUser().isParticipant
                ? () => openNotification('Sem permissão para envio de comentários', 'warn')
                : handleSendComment
            }
            disabled={isLoading}
            className="send"
          >
            <Icon $iconColor="primary" size={1} name="paperPlane" />
            Comentar
          </button>
        </div>
        <input onChange={handleFile} hidden type="file" multiple ref={inputRef} />
      </SendContainer>

      {isLoading && <Loading />}
      <ContainerCommentScroll>
        {comments.map(i => (
          <MessageContainer key={i.id}></MessageContainer>
        ))}
      </ContainerCommentScroll>
      <div ref={commentsCRef} className="content" onLoad={() => window.scrollTo(0, 0)}>
        {comments.map(i => (
          <MessageContainer key={i.id}>
            <header>
              <p className="title">{i.createdBy.name}</p>
              <span>{format(new Date(i.createdAt), 'dd/MM/yyyy - HH:mm')}</span>
            </header>
            <p className="content">{i.description}</p>
            {!!i.hasImage && (
              <div className="images">
                {i.anexosComentario
                  .filter(i => i.isImage)
                  .map(i => (
                    <img
                      onClick={handleDonwloadFile.bind(null, i.id, i.filename, i.url)}
                      alt={i.filename}
                      src={i.url}
                      key={i.id}
                    />
                  ))}
              </div>
            )}
            {!!i.hasDocument && (
              <div className="files">
                {i.anexosComentario
                  .filter(i => !i.isImage)
                  .map(i => (
                    <div
                      onClick={handleDonwloadFile.bind(null, i.id, i.filename, i.url)}
                      className="file"
                      key={i.id}
                    >
                      <Icon name="fileDonwload" size={1.5} color={500} />
                      <p>{i.filename}</p>
                    </div>
                  ))}
              </div>
            )}
          </MessageContainer>
        ))}
      </div>
    </Container>
  );
};

export const Comments = memo(CommentsTab);
